import React from 'react';

import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';

import { ClearIcon } from '@mui/x-date-pickers';

import Chip from 'components/Chip';
import { StyledTextField } from 'components/Input/styles';
import InfiniteAutocomplete from 'components/InfiniteAutocomplete';
import { limitChips } from 'components/Input/limitChips';

import { colors } from 'theme/colors';

import {
  isDisabledOption,
  isNavOption,
  memberLabel,
  PickerCategory,
  PickerItemKind,
  PickerMode,
  PickerNavKind,
  pickerOptionKey,
  PickerOption,
  PickerValue,
} from './types';

import { buildPickerOptions } from './options';
import { usePickerSearch } from './usePickerSearch';
import PickerOptionRow from './PickerOptionRow';

import { GroupChipIcon } from './styles';

const isSameOption = (a: PickerOption, b: PickerOption): boolean =>
  !isNavOption(a) && !isNavOption(b) && a.kind === b.kind && a.uuid === b.uuid;

interface UserAndGroupPickerProps {
  value: PickerValue[];
  onChange: (next: PickerValue[]) => void;
  enableGroups?: boolean;
  placeholder?: string;
  groupsPlaceholder?: string;
  error?: boolean;
  helperText?: string;
  licenses?: string;
  role?: string;
  excludeMemberGroupUuid?: string;
  emptyUsersText?: string;
  id?: string;
  predefinedOptions?: PickerValue[];
  // Options to render as checked in the dropdown without holding them in `value` — for a picker
  // whose committed selections live elsewhere (e.g. a list below) rather than as chips in the
  // field, so an already-added user/group still shows as checked instead of a silent re-pick.
  // Clicking one calls `onUncheckOption` rather than `onChange`, since from `value`'s perspective
  // (which never contains these) MUI would otherwise report it as a fresh addition, not a removal.
  checkedOptions?: PickerValue[];
  onUncheckOption?: (option: PickerValue) => void;
  size?: 'small' | 'medium';
  maxChips?: number;
  'data-testid'?: string;
}

const UserAndGroupPicker: React.FunctionComponent<UserAndGroupPickerProps> = ({
  value,
  onChange,
  enableGroups = false,
  placeholder: placeholderProp,
  groupsPlaceholder,
  error = false,
  helperText,
  licenses,
  role,
  excludeMemberGroupUuid,
  emptyUsersText = 'No users available',
  id = 'user-and-group-picker',
  predefinedOptions = [],
  checkedOptions = [],
  onUncheckOption,
  size = 'medium',
  maxChips,
  'data-testid': dataTestId,
}) => {
  const {
    groupsAvailable,
    mode,
    query,
    rows,
    loading,
    pending,
    hasMore,
    loadingMore,
    loadMore,
    openCategory,
    openUsers,
    goToRoot,
    closeCategory,
    search,
  } = usePickerSearch({
    enableGroups,
    licenses,
    role,
    excludeMemberGroupUuid,
    predefinedOptions,
  });

  const placeholder = groupsAvailable
    ? (groupsPlaceholder ?? placeholderProp ?? 'Add users or groups')
    : (placeholderProp ?? 'Add users');

  const options = buildPickerOptions({
    mode,
    enableGroups: groupsAvailable,
    rows,
    loading,
    pending,
    query,
    emptyUsersText,
  });

  const inputLabels: Record<PickerMode, { placeholder: string; ariaLabel: string }> = {
    root: { placeholder, ariaLabel: placeholder },
    [PickerCategory.Users]: groupsAvailable
      ? { placeholder: 'Search users…', ariaLabel: 'Search users' }
      : { placeholder, ariaLabel: placeholder },
    [PickerCategory.Groups]: { placeholder: 'Search groups…', ariaLabel: 'Search groups' },
  };
  const { placeholder: inputPlaceholder, ariaLabel: inputAriaLabel } = inputLabels[mode];

  const isChecked = (option: PickerOption): boolean =>
    !isNavOption(option) && [...value, ...checkedOptions].some((checked) => isSameOption(checked, option));

  return (
    <InfiniteAutocomplete
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={loadMore}
      multiple
      disableCloseOnSelect
      fullWidth
      size={size}
      id={id}
      data-testid={dataTestId}
      options={options}
      value={value as PickerOption[]}
      inputValue={query}
      loading={loading}
      onOpen={() => {
        if (groupsAvailable) {
          goToRoot();
        } else {
          openUsers();
        }
      }}
      onClose={() => {
        if (groupsAvailable) closeCategory();
      }}
      onInputChange={(_, next, reason) => {
        if (reason === 'reset') return;
        search(next);
      }}
      filterOptions={(opts) => opts}
      isOptionEqualToValue={isSameOption}
      getOptionLabel={(option: PickerOption) => (isNavOption(option) ? '' : option.name)}
      getOptionDisabled={isDisabledOption}
      onChange={(_, next: PickerOption[], __, details) => {
        const clicked = details?.option;
        if (clicked && clicked.kind === PickerNavKind.Category) {
          openCategory(clicked.category);
          return;
        }
        if (clicked && clicked.kind === PickerNavKind.Back) {
          goToRoot();
          return;
        }
        if (clicked && isDisabledOption(clicked)) {
          return;
        }
        if (clicked && !isNavOption(clicked)) {
          const checkedMatch = checkedOptions.find((c) => isSameOption(c, clicked));
          if (checkedMatch) {
            onUncheckOption?.(checkedMatch);
            return;
          }
        }
        onChange(next.filter((option) => !isNavOption(option)) as PickerValue[]);
      }}
      renderOption={(props, option: PickerOption) => (
        <PickerOptionRow
          {...props}
          key={pickerOptionKey(option)}
          option={option}
          query={query}
          checked={isChecked(option)}
        />
      )}
      renderTags={(tagValue: PickerOption[], getTagProps) => {
        const chips = tagValue.map((option, index) => {
          if (isNavOption(option)) return null;
          const isGroup = option.kind === PickerItemKind.Group;
          return (
            <Chip
              {...getTagProps({ index })}
              key={`${option.kind}-${option.uuid}`}
              label={isGroup ? `${option.name} · ${option.memberCount}` : option.name}
              aria-label={isGroup ? `${option.name}, ${memberLabel(option.memberCount)}` : option.name}
              variant="outlined"
              size="small"
              iconLeft={
                isGroup ? (
                  <GroupChipIcon>
                    <GroupsOutlinedIcon />
                  </GroupChipIcon>
                ) : undefined
              }
              colors={{
                background: colors.common.white,
                border: colors.ui.border.main,
              }}
              iconRight={<ClearIcon role="button" aria-label={`Remove ${option.name}`} />}
              iconRightDisableInnerMargin
              capitalizeLabel={false}
            />
          );
        });
        return maxChips === undefined ? chips : limitChips(chips, maxChips);
      }}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          placeholder={inputPlaceholder}
          error={error}
          helperText={helperText}
          FormHelperTextProps={{ sx: { whiteSpace: 'pre-line' } }}
          inputProps={{ ...params.inputProps, 'aria-label': inputAriaLabel }}
        />
      )}
    />
  );
};

export default UserAndGroupPicker;
